'use strict'

/**
 * マトリクス画面のオーケストレータ（フィーチャーストア Provider 配下）。 初期化（init）・各種ハンドラ・モーダル・派生データの取得を担い、 表示はメモ化されたセクション（MatrixControls /
 * MatrixSummary / MatrixFilters / MatrixLegend / MatrixTable）に委譲する。
 *
 * CorporationNumber / accPeriod はメインストアの値であり、Provider の外側で読み取った値を props で受け取る。
 */

import React, { useCallback, useEffect, useState, Profiler } from 'react'
import PropTypes from 'prop-types'
import imgCategoryDashboard from '../../../assets/img/category/img_category_dashboard.png'
import { fetchDataCreateTaskList } from '../extra/services'
import { fetchSettingJsonCached, fetchAllChrofyColumnsCached } from '../extra/matrixDataCache'
import { loadMatrixMappingPair } from '../extra/matrixMappingLoad'
import {
  buildLeftColumnFromSetting,
  buildHorizontalFromChrofyColumns,
  filterLeftColumnByModeAndPeriod,
  filterDetailPeriodByKind,
  getLatestDetailPeriodKey,
  mapDataCreateTaskListToCamelCase,
  groupRequiredColumnsForModal,
  computeRowFilterOptionsFromRf,
  cascadeRowFilters,
  periodTypeForFilter,
  buildAllRowFilters
} from '../extra/helpers'
import {
  MODE_STANDALONE,
  MODE_CONSOLIDATE,
  PERIOD_MONTHLY,
  PERIOD_QUARTER,
  PERIOD_YEAR,
  MODE_LABELS,
  PERIOD_LABELS
} from '../extra/constants'
import { ACTION_TYPES, useDashboardMatrixDispatch, useDashboardMatrixSelector } from '../extra/store'
import { useMatrixDerived } from '../extra/useMatrixDerived'
import { validateFilterExclusive } from '../extra/validation'
import { useModalExtra } from '../../../_components/Modal'
import { useRequiredColumnsModal } from './useRequiredColumnsModal'
import { useColumnInfoModal } from './useColumnInfoModal'
import MatrixControls from './MatrixControls'
import MatrixSummary from './MatrixSummary'
import MatrixFilters from './MatrixFilters'
import MatrixLegend from './MatrixLegend'
import MatrixTable from './MatrixTable'
import { logMatrixPerf } from '../extra/matrixPerf'
import { titleName } from '../../../_constants/Code'

/**
 * @param {{ corporationNumber: string; accPeriod: string }} props
 * @returns {JSX.Element}
 */
function MatrixBody({ corporationNumber, accPeriod }) {
  const dispatch = useDashboardMatrixDispatch()
  const { _closeUpModal } = useModalExtra()
  const showRequiredColumnsModal = useRequiredColumnsModal()
  const showColumnInfoModal = useColumnInfoModal()

  // モード切替（API を呼ばない同期処理）でもローディングオーバーレイを出すための一時フラグ。
  // mappingLoading とは別管理にすることで、行（MatrixRow）の再レンダーを誘発しない。
  const [switching, setSwitching] = useState(false)

  // ハンドラが参照する素の state スライス
  const mode = useDashboardMatrixSelector((s) => s.mode)
  const periodKind = useDashboardMatrixSelector((s) => s.periodKind)
  const taskListFull = useDashboardMatrixSelector((s) => s.taskListFull)
  const leftColumnRowsFull = useDashboardMatrixSelector((s) => s.leftColumnRowsFull)
  const currentRowFilters = useDashboardMatrixSelector((s) => s.filters.rowFilters)
  const mappingList = useDashboardMatrixSelector((s) => s.mappingList)
  const mappingListMonthly = useDashboardMatrixSelector((s) => s.mappingListMonthly)
  const loading = useDashboardMatrixSelector((s) => s.loading)
  const error = useDashboardMatrixSelector((s) => s.error)
  const mappingError = useDashboardMatrixSelector((s) => s.mappingError)
  const mappingLoading = useDashboardMatrixSelector((s) => s.mappingLoading)
  const apiWarning = useDashboardMatrixSelector((s) => s.apiWarning)

  // 派生データ（絞り込み・集計・事前計算）
  const {
    leftColumnRows,
    detailPeriodOptions,
    filteredColumns,
    leftColumnRowsFiltered,
    statusByCode,
    summary,
    deriving,
    totals,
    codeToName,
    rowFilterOptions
  } = useMatrixDerived()

  useEffect(() => {
    document.title = 'ダッシュボード×項目対応表' + titleName.common
  }, [])

  // モーダル背後のオーバーレイ（.bg-modal-window）クリックで閉じる。
  useEffect(() => {
    const handleOverlayClick = (e) => {
      if (e.target.classList.contains('bg-modal-window')) {
        _closeUpModal()
      }
    }
    document.addEventListener('click', handleOverlayClick, true)
    return () => document.removeEventListener('click', handleOverlayClick, true)
  }, [_closeUpModal])

  /** 画面の土台データを揃える。setting はローカル公開物のため先に取得し、その後に API 群をまとめる。 corporationNumber / accPeriod が変わるたびに再生成される。 */
  const init = useCallback(async () => {
    let settingJson
    try {
      settingJson = await fetchSettingJsonCached()
    } catch (err) {
      dispatch(ACTION_TYPES.INIT, {
        settingJson: null,
        leftColumnRowsFull: [],
        horizontalBlocks: [],
        taskListFull: [],
        periodKind: PERIOD_YEAR,
        detailPeriodKey: null,
        mappingList: [],
        mappingListMonthly: [],
        mappingError: null,
        mappingLoading: false,
        error: 'setting.jsonの取得に失敗しました',
        apiWarning: null
      })
      return
    }

    let chrofyColumns = []
    let taskList = []
    const apiErrors = []

    if (corporationNumber) {
      // CHROFY 項目とタスク一覧は互いに独立なので同時取得し、どちらか片方失敗でももう一方の結果は使う
      try {
        const [chrofyColumnsResult, taskListResult] = await Promise.all([
          fetchAllChrofyColumnsCached(corporationNumber).catch(() => {
            apiErrors.push('CHROFY項目の取得に失敗しました')
            return []
          }),
          fetchDataCreateTaskList(corporationNumber).catch(() => {
            apiErrors.push('データ作成タスクの取得に失敗しました')
            return []
          })
        ])
        chrofyColumns = Array.isArray(chrofyColumnsResult) ? chrofyColumnsResult : []
        taskList = mapDataCreateTaskListToCamelCase(Array.isArray(taskListResult) ? taskListResult : [])
      } catch (err) {
        apiErrors.push('APIの取得に失敗しました')
      }
    }

    const leftColumnRowsFullNext = buildLeftColumnFromSetting(settingJson)
    const horizontalBlocks = buildHorizontalFromChrofyColumns(chrofyColumns)
    const taskListFullNext = Array.isArray(taskList) ? taskList : []
    const periodKindNext = PERIOD_YEAR
    const options = filterDetailPeriodByKind(taskListFullNext, periodKindNext)
    const latestKey = getLatestDetailPeriodKey(options)

    let apiWarningNext = null
    if (apiErrors.length > 0) {
      apiWarningNext = apiErrors.join('、')
    }

    let mappingListNext = []
    let mappingListMonthlyNext = []
    let mappingErrorNext = null
    let mappingLoadingNext = false

    if (corporationNumber && latestKey != null) {
      mappingLoadingNext = true
      try {
        const pair = await loadMatrixMappingPair(
          corporationNumber,
          taskListFullNext,
          periodKindNext,
          latestKey,
          accPeriod
        )
        mappingListNext = pair.mappingList
        mappingListMonthlyNext = pair.mappingListMonthly
      } catch (err) {
        if (err?.response?.status === 404 || err?.response?.statusCode === 404) {
          mappingListNext = []
          mappingListMonthlyNext = []
        } else {
          mappingErrorNext = err?.message || '通信エラーが発生しました'
        }
      }
      mappingLoadingNext = false
    }

    dispatch(ACTION_TYPES.INIT, {
      settingJson,
      leftColumnRowsFull: leftColumnRowsFullNext,
      horizontalBlocks,
      taskListFull: taskListFullNext,
      periodKind: periodKindNext,
      detailPeriodKey: latestKey,
      mappingList: mappingListNext,
      mappingListMonthly: mappingListMonthlyNext,
      mappingError: mappingErrorNext,
      mappingLoading: mappingLoadingNext,
      error: null,
      apiWarning: apiWarningNext
    })
  }, [corporationNumber, accPeriod, dispatch])

  useEffect(() => {
    init()
  }, [init])

  const _loadMappingForKey = useCallback(
    async (key, overrides = {}) => {
      if (!corporationNumber || key == null) return
      const periodKindEff = overrides.periodKind ?? periodKind
      const taskListEff = overrides.taskListFull ?? taskListFull
      dispatch(ACTION_TYPES.SET_MAPPING_LOADING, true)
      try {
        const { mappingList: ml, mappingListMonthly: mlm } = await loadMatrixMappingPair(
          corporationNumber,
          taskListEff,
          periodKindEff,
          key,
          accPeriod
        )
        dispatch(ACTION_TYPES.SET_DUAL_MAPPING, { mappingList: ml, mappingListMonthly: mlm })
      } catch (err) {
        if (err?.response?.status === 404 || err?.response?.statusCode === 404) {
          dispatch(ACTION_TYPES.SET_DUAL_MAPPING, { mappingList: [], mappingListMonthly: [] })
        } else {
          dispatch(ACTION_TYPES.SET_MAPPING_ERROR, err?.message || '通信エラーが発生しました')
        }
      }
    },
    [corporationNumber, periodKind, taskListFull, accPeriod, dispatch]
  )

  const handleModeChange = useCallback(
    (e) => {
      const nextMode = e.target.value === MODE_LABELS[MODE_CONSOLIDATE] ? MODE_CONSOLIDATE : MODE_STANDALONE
      // 同じモードを再選択した場合は何もしない（連打による無駄な再レンダーを防ぐ）。
      if (nextMode === mode) return

      // モード変更時も行フィルタを「すべて」にリセット（レイアウトのジット防止）
      const periodType = periodTypeForFilter(periodKind)
      const newLeftColumnRows = filterLeftColumnByModeAndPeriod(leftColumnRowsFull, nextMode, periodType)
      const allRowFilters = buildAllRowFilters(newLeftColumnRows)

      // 1) 先にローディングオーバーレイを表示（軽いコミット）。2) 次フレームで重い再フィルタ・再レンダーを実行し、
      //    さらに次フレームでオーバーレイを消す。これでオーバーレイが先に描画され、固まったように見えない。
      setSwitching(true)
      requestAnimationFrame(() => {
        dispatch(ACTION_TYPES.SET_MODE, nextMode)
        dispatch(ACTION_TYPES.SET_FILTERS, { rowFilters: allRowFilters })
        requestAnimationFrame(() => setSwitching(false))
      })
    },
    [mode, periodKind, leftColumnRowsFull, dispatch]
  )

  const handlePeriodKindChange = useCallback(
    async (e) => {
      const nextPeriodKind =
        e.target.value === PERIOD_LABELS[PERIOD_MONTHLY]
          ? PERIOD_MONTHLY
          : e.target.value === PERIOD_LABELS[PERIOD_QUARTER]
            ? PERIOD_QUARTER
            : PERIOD_YEAR
      // 同じ対象期間を再選択した場合は何もしない（連打防止）。
      if (nextPeriodKind === periodKind) return

      // 1) 即時反映: 対象期間ラベルを更新し、テーブルをローディング表示にする。API 待ちでもプルダウンは先に切り替わる。
      //    詳細期間 Key は新しいタスク一覧の取得後に確定するため、一旦 null（詳細期間は「選択してください」表示）。
      dispatch(ACTION_TYPES.SET_PERIOD_KIND, nextPeriodKind, { detailPeriodKey: null })
      dispatch(ACTION_TYPES.SET_MAPPING_LOADING, true)

      // 2) 新しいタスク一覧を取得
      let taskListFullNext = taskListFull
      if (corporationNumber) {
        try {
          const list = await fetchDataCreateTaskList(corporationNumber)
          taskListFullNext = mapDataCreateTaskListToCamelCase(Array.isArray(list) ? list : [])
          dispatch(ACTION_TYPES.SET_TASK_LIST, taskListFullNext)
        } catch (err) {
          dispatch(ACTION_TYPES.SET_MAPPING_ERROR, err?.message || '通信エラーが発生しました')
          return
        }
      }
      const options = filterDetailPeriodByKind(taskListFullNext, nextPeriodKind)
      const latestKey = getLatestDetailPeriodKey(options)

      // 3) 詳細期間・行フィルタを確定し、マッピングを読み込む（読み込み完了で SET_DUAL_MAPPING がローディングを解除）。
      dispatch(ACTION_TYPES.SET_DETAIL_PERIOD, latestKey)
      const periodType = periodTypeForFilter(nextPeriodKind)
      const newLeftColumnRows = filterLeftColumnByModeAndPeriod(leftColumnRowsFull, mode, periodType)
      const allRowFilters = buildAllRowFilters(newLeftColumnRows)
      dispatch(ACTION_TYPES.SET_FILTERS, { rowFilters: allRowFilters })

      await _loadMappingForKey(latestKey, { periodKind: nextPeriodKind, taskListFull: taskListFullNext })
    },
    [periodKind, corporationNumber, taskListFull, leftColumnRowsFull, mode, _loadMappingForKey, dispatch]
  )

  const handleDetailPeriodChange = useCallback(
    async (e) => {
      const key = e.target.value === '' ? null : Number(e.target.value)
      dispatch(ACTION_TYPES.SET_DETAIL_PERIOD, key)

      const periodType = periodTypeForFilter(periodKind)
      const newLeftColumnRows = filterLeftColumnByModeAndPeriod(leftColumnRowsFull, mode, periodType)
      const allRowFilters = buildAllRowFilters(newLeftColumnRows)
      dispatch(ACTION_TYPES.SET_FILTERS, { rowFilters: allRowFilters })

      await _loadMappingForKey(key, { taskListFull })
    },
    [periodKind, taskListFull, leftColumnRowsFull, mode, _loadMappingForKey, dispatch]
  )

  const handleFilterChange = useCallback(
    (payload) => {
      if (payload.checkedSet !== undefined && payload.checkedUnset !== undefined) {
        const { checkedSet, checkedUnset } = validateFilterExclusive(payload.checkedSet, payload.checkedUnset)
        dispatch(ACTION_TYPES.SET_FILTERS, { ...payload, checkedSet, checkedUnset })
        return
      }
      if (payload.rowFilters != null) {
        const mergedRf = { ...currentRowFilters, ...payload.rowFilters }
        const prevOpt = computeRowFilterOptionsFromRf(leftColumnRows, currentRowFilters)
        const nextOpt = computeRowFilterOptionsFromRf(leftColumnRows, mergedRf)
        const cascaded = cascadeRowFilters(mergedRf, nextOpt, prevOpt)
        dispatch(ACTION_TYPES.SET_FILTERS, {
          ...payload,
          rowFilters: { ...mergedRf, ...cascaded }
        })
        return
      }
      dispatch(ACTION_TYPES.SET_FILTERS, payload)
    },
    [leftColumnRows, currentRowFilters, dispatch]
  )

  const handleGraphClick = useCallback(
    (row) => {
      const requiredColumnsGrouped = groupRequiredColumnsForModal(
        row.required_columns,
        mappingList,
        codeToName,
        mappingListMonthly,
        periodKind,
        row.optional_columns
      )
      const dashboardName = row.dashboardCode
        ? `${row.dashboardCode} ${row.dashboardName}`.trim()
        : (row.dashboardName ?? '')
      showRequiredColumnsModal({
        dashboardName,
        graphName: row.graphName ?? '',
        periodKind,
        requiredColumnsGrouped: requiredColumnsGrouped ?? { set: [], unset: [], remove: [] }
      })
    },
    [mappingList, mappingListMonthly, periodKind, codeToName, showRequiredColumnsModal]
  )

  const handleColumnInfoClick = useCallback((col) => showColumnInfoModal(col), [showColumnInfoModal])

  return (
    <main className="page-container dashboard-item-matrix-page">
      <section className="page-content">
        <div className="centered-container">
          <header className="category-page-header flex items-center my-6">
            <div className="category-page-header__img ml-4 mr-8">
              <img src={imgCategoryDashboard} alt="" />
            </div>
            <div className="category-page-header__text pl-8 relative">
              <div className="category-page-header__subtitle flex items-center red--text">
                <span className="category-page-header__subtitle-text text-h6-en category-page-header__subtitle-text--no-line">
                  Dashboard Item Matrix
                </span>
              </div>
              <h1 className="category-page-header__title">
                <span className="category-page-header__title-text text-h2">ダッシュボード×項目対応表</span>
              </h1>
              <p className="category-page-header__description text-caption-2 grey--text text--lighten-70">
                全てのダッシュボードとCHROFY項目の対応関係を確認。データ項目の登録状況も把握できます。
              </p>
            </div>
          </header>

          {apiWarning && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
              <p className="text-sm">⚠️ {apiWarning}</p>
            </div>
          )}

          {mappingError && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded text-sm" role="alert">
              {mappingError}
            </div>
          )}

          <div className="page-body dashboard-item-matrix-page-body">
            <div
              className="box-xy-bordered rounded-t-3xl rounded-b-2xl mt-8 pt-10 pb-16 px-8 border-blue1"
              style={{ overflowX: 'hidden', minWidth: 0 }}
            >
              <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-8 rounded-t-2xl">
                ダッシュボード × 標準フォーマット項目対応表
              </h2>

              <div className="matrix-wrapper mt-10">
                <div className="controls px-5 pt-3 pb-2 border-b border-[#d6dbe8] bg-white rounded-t-3xl">
                  <MatrixControls
                    detailPeriodOptions={detailPeriodOptions}
                    onModeChange={handleModeChange}
                    onPeriodKindChange={handlePeriodKindChange}
                    onDetailPeriodChange={handleDetailPeriodChange}
                  />
                  <MatrixSummary summary={summary} totals={totals} />
                  <MatrixFilters rowFilterOptions={rowFilterOptions} onFilterChange={handleFilterChange} />
                </div>

                <MatrixLegend />

                {/* Profiler は計測（window.__MATRIX_PERF__=true）時のみログ出力。通常時は透過的で副作用なし。 */}
                <Profiler
                  id="dashboard-matrix-table"
                  onRender={(id, phase, actualDuration) => logMatrixPerf(`render(${phase})`, actualDuration)}
                >
                  <MatrixTable
                    filteredColumns={filteredColumns}
                    leftColumnRowsFiltered={leftColumnRowsFiltered}
                    statusByCode={statusByCode}
                    rowFilterOptions={rowFilterOptions}
                    loading={loading}
                    error={error}
                    mappingLoading={mappingLoading}
                    overlayLoading={mappingLoading || switching || deriving}
                    onFilterChange={handleFilterChange}
                    onGraphClick={handleGraphClick}
                    onColumnInfoClick={handleColumnInfoClick}
                  />
                </Profiler>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

MatrixBody.propTypes = {
  corporationNumber: PropTypes.string,
  accPeriod: PropTypes.string
}

MatrixBody.defaultProps = {
  corporationNumber: '',
  accPeriod: ''
}

export default MatrixBody
